import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import React from 'react';
import seo from '../../data';
const privacy = {
	title: 'Privacy Policy',
	introduction: `At ${seo.companyName}, we are committed to protecting your privacy. This Privacy Policy outlines how we collect, use, and safeguard your personal information when you visit our website or contact us through our contact form.`,
	sections: [
		{
			title: 'Information We Collect',
			content: [
				{
					type: 'Personal Information',
					details:
						'When you fill out our contact form, we may collect your name, email address, phone number, and any other information you provide.',
				},
				{
					type: 'Usage Information',
					details:
						'We may collect non-personal information about your visit to our website, including the pages you view, the links you click, and other actions you take.',
				},
			],
		},
		{
			title: 'How We Use Your Information',
			content: [
				{
					purpose: 'Personalization',
					details:
						'We may use your information to personalize your experience and provide you with relevant content and services.',
				},
				{
					purpose: 'Communication',
					details:
						'We may use your contact information to respond to your inquiries, provide assistance, and communicate with you about our services.',
				},
				{
					purpose: 'Analytics',
					details:
						"We may use usage information to analyze trends, track website traffic, and improve our website's performance and user experience.",
				},
			],
		},
		{
			title: 'Information Sharing',
			content:
				'We do not sell, trade, or rent your personal information to third parties. However, we may share your information with trusted service providers who assist us in operating our website and conducting our business.',
		},
		{
			title: 'Data Security',
			content:
				'We implement security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, please be aware that no method of transmission over the internet or electronic storage is 100% secure.',
		},
		{
			title: 'Your Choices',
			content:
				'You may choose not to provide certain personal information, but this may limit your ability to access certain features of our website or receive our services. You can opt out of receiving marketing communications from us by following the unsubscribe instructions included in each email.',
		},
		{
			title: 'Changes to This Policy',
			content:
				'We reserve the right to update or modify this Privacy Policy at any time. Any changes will be effective immediately upon posting on this page.',
		},
	],
	contact: {
		title: 'Contact Us',
		content: `If you have any questions or concerns about this Privacy Policy, please contact us at ${seo.company.email}`,
	},
};

export default function PrivacyPolicy() {
	return (
		<div className='bg-white'>
			<div className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 lg:py-40'>
				<div className='mx-auto max-w-4xl divide-y divide-gray-900/10 mb-10'>
					<h2 className='text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl '>
						{privacy.title}
					</h2>
					<p className='mt-4 text-xl text-gray-500'>{privacy.introduction}</p>
					<dl className='mt-10 space-y-6 divide-y divide-gray-900/10'>
						{privacy.sections.map((section, index) => (
							<Disclosure
								key={index}
								as='div'
								className='pt-6'
							>
								<dt>
									<DisclosureButton className='group flex w-full items-start justify-between text-left text-gray-900'>
										<span className='text-base/7 font-semibold'>{section.title}</span>
										<span className='ml-6 flex h-7 items-center'>
											<PlusIcon
												aria-hidden='true'
												className='h-6 w-6 group-data-[open]:hidden'
											/>
											<MinusIcon
												aria-hidden='true'
												className='h-6 w-6 [.group:not([data-open])_&]:hidden'
											/>
										</span>
									</DisclosureButton>
								</dt>
								<DisclosurePanel
									as='dd'
									className='my-2 pr-12'
								>
									<div className='text-base/7 text-gray-600'>
										{Array.isArray(section.content)
											? section.content.map((item, itemIndex) => (
													<p key={itemIndex}>
														{item.type ? `${item.type}: ${item.details}` : item.details}
													</p>
											  ))
											: section.content}
									</div>
								</DisclosurePanel>
							</Disclosure>
						))}
					</dl>
					<div className='mt-12'>
						<h3 className='text-xl font-bold text-gray-900'>{privacy.contact.title}</h3>
						<p className='text-gray-600'>{privacy.contact.content}</p>
					</div>
				</div>
			</div>
		</div>
	);
}
